import { Icon } from '../Icon';
import { taskCategoryLabel, formatCoworkerNames } from '../../lib/taskProcess';
import type { Member, SessionUser, TaskRecord } from '../../types';

interface TasksTabProps {
    sessionUser: SessionUser;
    visibleTasks: TaskRecord[];
    membersById: Record<string, Member>;
    onOpenRegister: () => void;
    handleUpdateStatus: (taskId: string, status: string) => void;
}

export function TasksTab({ sessionUser, visibleTasks, membersById, onOpenRegister, handleUpdateStatus }: TasksTabProps) {
    return (
        <div>
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h2 className="text-lg font-bold text-slate-900">연초 과제 Assign 및 등록 현황</h2>
                    <p className="text-sm text-slate-500">
                        {sessionUser.role === 'EMPLOYEE'
                            ? '본인 중점과제 등록 · Category 지정 · 협업 동료(Co-worker) 매핑'
                            : '조직 구성원 과제 Assign(승인/반려) 및 Category 현황'}
                    </p>
                </div>
                {sessionUser.role === 'EMPLOYEE' && (
                    <button
                        onClick={onOpenRegister}
                        className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2.5 rounded-lg text-sm font-semibold shadow-md transition-all flex items-center gap-2"
                    >
                        <Icon name="plus-circle" className="w-4 h-4" /> 신규 중점 업무 등록
                    </button>
                )}
            </div>

            {visibleTasks.length === 0 && (
                <div className="bg-white border border-dashed border-slate-300 rounded-xl p-10 text-center text-slate-400 text-sm">
                    아직 등록된 중점 과제가 없습니다.
                    {sessionUser.role === 'EMPLOYEE' && ' 우측 상단의 [신규 중점 업무 등록] 버튼으로 연간 과제를 등록해주세요.'}
                </div>
            )}

            <div className="grid grid-cols-1 gap-6">
                {visibleTasks.map(task => (
                    <div key={task.id} className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm hover:shadow-md transition-all">
                        <div className="flex justify-between items-start gap-4 mb-4">
                            <div className="flex items-center gap-2 flex-wrap">
                                <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${task.type === 'QUANTITATIVE' ? 'bg-amber-50 text-amber-700 border border-amber-200' : 'bg-emerald-50 text-emerald-700 border border-emerald-200'}`}>
                                    {task.type === 'QUANTITATIVE' ? '정량 업무' : '정성 업무'}
                                </span>
                                <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-violet-50 text-violet-700 border border-violet-200">
                                    {taskCategoryLabel(task.taskCategory)}
                                </span>
                                <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${task.status === 'APPROVED' ? 'bg-indigo-50 text-indigo-700' : task.status === 'REJECTED' ? 'bg-rose-50 text-rose-700' : 'bg-slate-100 text-slate-600'}`}>
                                    {task.status === 'APPROVED' ? 'Assign 승인' : task.status === 'REJECTED' ? '반려됨' : 'Assign 대기'}
                                </span>
                                {sessionUser.role !== 'EMPLOYEE' && task.ownerName && (
                                    <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-slate-900 text-white">
                                        {task.ownerName} · {task.ownerDept || '소속 미상'}
                                    </span>
                                )}
                            </div>

                            {sessionUser.role === 'EVALUATOR' && task.status === 'REGISTERED' && (
                                <div className="flex gap-2 flex-shrink-0">
                                    <button
                                        onClick={() => handleUpdateStatus(task.id, 'APPROVED')}
                                        className="bg-indigo-600 text-white text-xs px-3 py-1.5 rounded-lg font-bold hover:bg-indigo-700 flex items-center gap-1.5"
                                    >
                                        <Icon name="check" className="w-3 h-3" /> 승인
                                    </button>
                                    <button
                                        onClick={() => handleUpdateStatus(task.id, 'REJECTED')}
                                        className="bg-rose-100 text-rose-700 text-xs px-3 py-1.5 rounded-lg font-bold hover:bg-rose-200 flex items-center gap-1.5"
                                    >
                                        <Icon name="x" className="w-3 h-3" /> 반려
                                    </button>
                                </div>
                            )}
                        </div>

                        <h3 className="text-base font-bold text-slate-900 mb-2">{task.title}</h3>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4 pt-4 border-t border-slate-100 text-sm">
                            <div>
                                <span className="text-slate-400 block text-xs">목표/기대 수준</span>
                                <span className="font-medium text-slate-700">{task.targetValue}</span>
                            </div>
                            <div>
                                <span className="text-slate-400 block text-xs">수행 기한</span>
                                <span className="font-medium text-slate-700">{task.period}</span>
                            </div>
                            <div>
                                <span className="text-slate-400 block text-xs">협업 동료 (Co-worker)</span>
                                <span className="font-medium text-slate-700">
                                    {formatCoworkerNames(task.coworkerIds, membersById, task.coworkers)}
                                </span>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
